// Service Worker Utilities
// This file provides utilities to manage service workers, especially for development
// Includes Edge-specific compatibility fixes

/**
 * Detect if running in Microsoft Edge
 */
export const isEdge = () => {
	return /Edg/.test(navigator.userAgent);
};

/**
 * Detect if running in a secure context (required for service workers)
 */
export const isSecureContext = () => {
	return window.isSecureContext || location.protocol === 'https:' || location.hostname === 'localhost';
};

/**
 * Unregister all service workers
 * Useful for development when service workers are causing issues
 * Includes Edge-specific handling
 */
export const unregisterServiceWorkers = async () => {
	if ('serviceWorker' in navigator) {
		try {
			const registrations = await navigator.serviceWorker.getRegistrations();

			for (let registration of registrations) {
				console.log('Unregistering service worker:', registration.scope);
				await registration.unregister();
			}

			console.log('All service workers unregistered');
			return true;
		} catch (error) {
			console.error('Error unregistering service workers:', error);
			return false;
		}
	} else {
		console.warn('Service workers not supported in this browser');
		return false;
	}
};

/**
 * Clear all caches
 * Useful when service workers are caching old resources
 */
export const clearAllCaches = async () => {
	if ('caches' in window) {
		try {
			const cacheNames = await caches.keys();

			for (let cacheName of cacheNames) {
				console.log('Deleting cache:', cacheName);
				await caches.delete(cacheName);
			}

			console.log('All caches cleared');
			return true;
		} catch (error) {
			console.error('Error clearing caches:', error);
			return false;
		}
	}
	return false;
};

/**
 * Reset service worker state completely
 * Unregisters all service workers and clears all caches
 */
export const resetServiceWorkerState = async () => {
	console.log('Resetting service worker state...');

	const unregistered = await unregisterServiceWorkers();
	const cleared = await clearAllCaches();

	if (unregistered && cleared) {
		console.log('Service worker state reset successfully');
		return true;
	} else {
		console.warn('Service worker state reset completed with some issues');
		return false;
	}
};


/**
 * Configure Workbox logging level
 * @param {string} level - 'debug', 'log', 'warn', 'error', or 'silent'
 */
export const setWorkboxLogLevel = (level = 'warn') => {
	if (typeof window !== 'undefined' && window.workbox) {
		window.workbox.setConfig({ debug: level === 'debug' });
		console.log(`Workbox log level set to: ${level}`);
	} else {
		console.warn('Workbox not available or not loaded yet');
	}
};

/**
 * Disable Workbox console messages
 */
export const disableWorkboxLogging = () => {
	setWorkboxLogLevel('silent');
};

/**
 * Enable Workbox debug logging
 */
export const enableWorkboxDebugging = () => {
	setWorkboxLogLevel('debug');
};

/**
 * Check for Workbox service workers
 */
export const checkWorkboxServiceWorkers = async () => {
	if ('serviceWorker' in navigator) {
		try {
			const registrations = await navigator.serviceWorker.getRegistrations();
			const workboxSWs = registrations.filter(reg =>
				reg.active?.scriptURL?.includes('workbox') ||
				reg.scope.includes('workbox')
			);

			return {
				workboxFound: workboxSWs.length > 0,
				registrations: workboxSWs,
				totalSWs: registrations.length
			};
		} catch (error) {
			console.error('Error checking Workbox service workers:', error);
			return { workboxFound: false, error: error.message };
		}
	}
	return { workboxFound: false, error: 'Service workers not supported' };
};

/**
 * Check for stale service workers (using old SDK versions)
 * Returns list of service workers that need to be updated
 */
export const checkForStaleServiceWorkers = async () => {
	if ('serviceWorker' in navigator) {
		try {
			const registrations = await navigator.serviceWorker.getRegistrations();
			const staleSWs = [];

			for (const registration of registrations) {
				const scriptURL = registration.active?.scriptURL || registration.installing?.scriptURL || '';

				// Check for service workers that might be causing issues
				if (registration.active && registration.waiting) {
					// There's a waiting service worker that hasn't activated
					staleSWs.push({
						registration,
						reason: 'Service worker update pending but not activated',
						scriptURL
					});
				}
			}

			return {
				hasStale: staleSWs.length > 0,
				staleWorkers: staleSWs,
				totalRegistrations: registrations.length
			};
		} catch (error) {
			console.error('Error checking for stale service workers:', error);
			return { hasStale: false, error: error.message };
		}
	}
	return { hasStale: false, error: 'Service workers not supported' };
};

/**
 * Force update all service workers
 */
export const forceUpdateServiceWorkers = async () => {
	if ('serviceWorker' in navigator) {
		try {
			const registrations = await navigator.serviceWorker.getRegistrations();

			for (const registration of registrations) {
				try {
					// Force update by calling update()
					await registration.update();

					// If there's a waiting worker, skip waiting and activate it
					if (registration.waiting) {
						registration.waiting.postMessage({ type: 'SKIP_WAITING' });
					}

					console.log('Forced update for service worker:', registration.scope);
				} catch (error) {
					console.warn('Failed to update service worker:', registration.scope, error);
				}
			}

			return true;
		} catch (error) {
			console.error('Error forcing service worker updates:', error);
			return false;
		}
	}
	return false;
};

/**
 * Clean up stale service workers automatically
 * This should be called on app initialization to fix cached service worker issues
 */
export const cleanupStaleServiceWorkers = async () => {
	if (!isSecureContext()) {
		return { cleaned: false, reason: 'Not a secure context' };
	}

	try {
		const staleCheck = await checkForStaleServiceWorkers();

		if (staleCheck.hasStale && staleCheck.staleWorkers) {
			console.log('Found stale service workers, cleaning up...', staleCheck.staleWorkers);

			// Unregister stale service workers
			for (const stale of staleCheck.staleWorkers) {
				try {
					await stale.registration.unregister();
					console.log('Unregistered stale service worker:', stale.reason);
				} catch (error) {
					console.warn('Failed to unregister stale service worker:', error);
				}
			}

			// Clear caches after unregistering
			await clearAllCaches();

			return {
				cleaned: true,
				unregistered: staleCheck.staleWorkers.length,
				message: 'Stale service workers cleaned up'
			};
		}

		return { cleaned: false, reason: 'No stale service workers found' };
	} catch (error) {
		console.error('Error cleaning up stale service workers:', error);
		return { cleaned: false, error: error.message };
	}
};

// Development helper - expose to window for debugging
if (process.env.NODE_ENV === 'development') {
	window.serviceWorkerUtils = {
		unregisterServiceWorkers,
		clearAllCaches,
		resetServiceWorkerState,
		// Workbox utilities
		setWorkboxLogLevel,
		disableWorkboxLogging,
		enableWorkboxDebugging,
		checkWorkboxServiceWorkers,
		// Stale service worker management
		checkForStaleServiceWorkers,
		forceUpdateServiceWorkers,
		cleanupStaleServiceWorkers,
		// Browser detection
		isEdge,
		isSecureContext
	};
}
